import QUERY_KEYS from '@app/common/queryKeys';
import { useAuthContext } from '@context/auth/useAuthContext';
import useNotifications from '@context/notifications/useNotificationContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  completePlanningSession,
  discardPlanningSession,
  getCurrentPlanningSession,
  openPlanningSession,
  PlanningSession,
} from '../api/planningSession';

/**
 * The one source of truth for whether a planning session is open.
 *
 * Both surfaces that show it — the bar in `MasterLayout` and the toggle on the scheduling
 * calendar — read this hook rather than holding their own state. A second source of truth is
 * how they stop agreeing, and a toggle that says "off" over a bar that says "12 changes
 * waiting" is worse than either alone.
 */

/** 60s. The count moves as the planner works; the clock is a local tick, not this. */
const POLL_INTERVAL_MS = 60 * 1000;

export interface UsePlanningSessionResult {
  session: PlanningSession | null;
  isLoading: boolean;
  /**
   * Opens one, or returns the one already open.
   *
   * `mutate`, not `mutateAsync`: every call site fires and forgets, and a rejected promise
   * nobody awaited is an unhandled rejection. The axios interceptor already toasts the
   * failure (`ApiInterceptorProvider` calls `handleError` with `forceShow`), so what these
   * would add is console noise, not feedback.
   */
  openSession: () => void;
  /** Closes and flushes — every affected caregiver gets one summary. */
  completeSession: (planningSessionId: number) => void;
  /** Closes without flushing. Sends nothing. */
  discardSession: (planningSessionId: number) => void;
  isOpening: boolean;
  isCompleting: boolean;
  isDiscarding: boolean;
}

export const usePlanningSession = (): UsePlanningSessionResult => {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { openNotification } = useNotifications();
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.PLANNING_SESSION_CURRENT],
    queryFn: getCurrentPlanningSession,
    // The endpoint is admin-only and the doctor portal has no scheduling surface, so for a
    // doctor this poll is a 403 a minute for the life of the tab. Gated here rather than at
    // the header because four components mount this hook.
    enabled: !user.isDoctor,
    refetchInterval: POLL_INTERVAL_MS,
    // `refetchOnWindowFocus` is globally false (App.tsx). Turned back on here because a
    // planner returning to the tab must see a current count, not the one from when they left.
    refetchOnWindowFocus: true,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PLANNING_SESSION_CURRENT] }),
    [queryClient]
  );

  const openMutation = useMutation({ mutationFn: openPlanningSession, onSuccess: invalidate });
  /**
   * A publish where every send failed comes back 200 with the session still open, so the
   * axios interceptor toasts nothing and the bar merely reappears. Without this the planner
   * reads that as published.
   */
  const completeMutation = useMutation({
    mutationFn: completePlanningSession,
    onSuccess: (flush) => {
      invalidate();
      // Optional-chained: a body-less 200 must not throw inside onSuccess.
      if (flush?.published === false) {
        openNotification('topRight', {
          title: t('planning-session-publish-failed-title'),
          description: t('planning-session-publish-failed-desc'),
          type: 'Danger',
        });
      }
    },
  });
  const discardMutation = useMutation({ mutationFn: discardPlanningSession, onSuccess: invalidate });

  return {
    session: data ?? null,
    isLoading,
    openSession: openMutation.mutate,
    completeSession: completeMutation.mutate,
    discardSession: discardMutation.mutate,
    isOpening: openMutation.isPending,
    isCompleting: completeMutation.isPending,
    isDiscarding: discardMutation.isPending,
  };
};

export default usePlanningSession;
